<script setup lang="ts">
import { Search } from 'lucide-vue-next'
import ProductRow from '@/components/product/ProductRow.vue'

import ExternalSkuInput from '@/views/reward/components/ExternalSkuInput.vue'
import { parseSkuCsv } from '@/utils/sku'
import { initializeQuantities } from '@/utils/productHelpers'

import type { Partner, PartnerProduct } from '@/types/types'

interface Props {
  isLoading: boolean
  partners: Partner[]
  associatedProducts: PartnerProduct[]
  readonly?: boolean
  helpUrl?: string
  errorMessage?: string | null
  showRequiredQuantity?: boolean
  title?: string
  automaticInputTitle?: string
  noPartnerText?: string
  skuInputTitle?: string
  skuInputPlaceholder?: string
  skuInputHelpText?: string
}

const props = withDefaults(defineProps<Props>(), {
  readonly: false,
  helpUrl: undefined,
  errorMessage: null,
  showRequiredQuantity: false,
  title: undefined,
  automaticInputTitle: undefined,
  noPartnerText: undefined,
  skuInputTitle: undefined,
  skuInputPlaceholder: undefined,
  skuInputHelpText: undefined
})

const emit = defineEmits<{
  (e: 'open-modal', partner: Partner): void
  (e: 'remove-product', product: PartnerProduct): void
  (e: 'configure'): void
}>()

defineSlots<{
  header(): unknown
  footer(): unknown
}>()

const productSkusCsv = defineModel<string | null>('productSkusCsv', { default: null })
const productQuantities = defineModel<Record<string, number>>('productQuantities', { default: () => ({}) })

const { t } = useI18n()

const effectiveTitle = computed(() => props.title ?? t('components.productAssociation.title'))
const effectiveAutomaticInputTitle = computed(
  () => props.automaticInputTitle ?? t('components.productAssociation.automaticInputTitle')
)
const effectiveNoPartnerText = computed(
  () => props.noPartnerText ?? t('components.productAssociation.noPartnerAvailable')
)
const effectiveSkuInputTitle = computed(
  () => props.skuInputTitle ?? t('components.productAssociation.externalSkuTitle')
)
const effectiveSkuInputPlaceholder = computed(
  () => props.skuInputPlaceholder ?? t('components.productAssociation.externalIdPlaceholder')
)
const effectiveSkuInputHelpText = computed(
  () => props.skuInputHelpText ?? t('components.productAssociation.externalSkuHelpText')
)

const manualSkus = computed(() => parseSkuCsv(productSkusCsv.value))

const enrichedProducts = computed(() =>
  props.associatedProducts
    .filter((product) => {
      const partnerId = Number(product.partnerId)
      const externalId = String(product.externalId ?? '').trim()
      return Number.isFinite(partnerId) && partnerId > 0 && externalId.length > 0
    })
    .map((product) => ({
      ...product,
      partner: props.partners.find((p) => p.id === product.partnerId)
    }))
)

// For Lightspeed (and other multi-store POS), the merchant attaches N per-store variants
// of the same logical product. Showing N rows in the persistent list is unreadable, so
// we collapse them into one row per (partnerId, name). The original items are kept
// inside the group so removal and per-item quantities still flow back correctly.
type ProductGroup = {
  key: string
  name: string
  partnerId: number
  partner: Partner | undefined
  image: string | null
  externalIds: string[]
  products: Array<PartnerProduct & { partner?: Partner }>
}

const groupedAssociatedProducts = computed<ProductGroup[]>(() => {
  const groups = new Map<string, ProductGroup>()
  for (const item of enrichedProducts.value) {
    const groupKey = `${item.partnerId}::${item.name.trim().toLowerCase()}`
    let group = groups.get(groupKey)
    if (!group) {
      group = {
        key: groupKey,
        name: item.name,
        partnerId: item.partnerId,
        partner: item.partner,
        image: item.image ?? null,
        externalIds: [],
        products: []
      }
      groups.set(groupKey, group)
    }
    group.externalIds.push(String(item.externalId))
    group.products.push(item)
  }
  return Array.from(groups.values())
})

const hasProducts = computed(() => groupedAssociatedProducts.value.length > 0 || manualSkus.value.length > 0)

const tableGridClass = computed(() => {
  if (props.showRequiredQuantity) return 'grid-cols-[minmax(0,1fr)_112px_5rem]'
  if (!props.readonly) return 'grid-cols-[1fr_1.75rem]'
  return 'grid-cols-[1fr]'
})

const getProductKey = (product: PartnerProduct) => `${product.partnerId}-${product.externalId}`
const getManualSkuKey = (sku: string) => `manual-${sku}`

const getQuantity = (key: string) => productQuantities.value[key] ?? 1

const getManualSkuQuantity = (sku: string) => getQuantity(getManualSkuKey(sku))

// A grouped row shows one quantity input that applies to every per-store variant in
// the group. We read the quantity off the first member (initializeQuantities keeps the
// keys aligned across members of the same group) and propagate any change to all.
const getGroupQuantity = (group: ProductGroup) =>
  group.products.length > 0 ? getQuantity(getProductKey(group.products[0])) : 1

const changeQuantity = (key: string, delta: number) => {
  const next = Math.max(1, getQuantity(key) + delta)
  productQuantities.value = { ...productQuantities.value, [key]: next }
}

const changeGroupQuantity = (group: ProductGroup, delta: number) => {
  const next = Math.max(1, getGroupQuantity(group) + delta)
  const updates: Record<string, number> = {}
  for (const p of group.products) {
    updates[getProductKey(p)] = next
  }
  productQuantities.value = { ...productQuantities.value, ...updates }
}

const handleRemoveSku = (skuToRemove: string) => {
  const updated = manualSkus.value.filter((s) => s !== skuToRemove)
  productSkusCsv.value = updated.length > 0 ? updated.join(',') : null
}

const handleRemoveGroup = (group: ProductGroup) => {
  for (const product of group.products) {
    emit('remove-product', product)
  }
}
const handleOpenModal = (partner: Partner) => emit('open-modal', partner)

const failedImages = reactive(new Set<string>())

watch(
  [() => props.associatedProducts, manualSkus],
  ([products, skus]) => {
    if (!props.showRequiredQuantity) return

    const allKeys = [...products.map(getProductKey), ...skus.map(getManualSkuKey)]
    let next = initializeQuantities(allKeys, productQuantities.value)

    // After init, align every per-store variant in a group to the same canonical
    // (max) quantity. Without this, a newly attached store variant inherits the
    // default `1` from initializeQuantities while its siblings keep their previous
    // higher value, so the grouped row shows one number but `productQuantities`
    // saves a mix. The user-visible quantity and the saved data end up disagreeing.
    for (const group of groupedAssociatedProducts.value) {
      if (group.products.length <= 1) continue
      const keys = group.products.map(getProductKey)
      const baseline = next ?? productQuantities.value
      const canonical = keys.reduce((max, k) => Math.max(max, baseline[k] ?? 1), 1)
      for (const k of keys) {
        if (baseline[k] !== canonical) {
          next = next ?? { ...productQuantities.value }
          next[k] = canonical
        }
      }
    }

    if (next) productQuantities.value = next
  },
  { immediate: true, deep: true }
)
</script>

<template>
  <div class="space-y-4 rounded-xl border border-border bg-white p-6 text-xs">
    <div class="flex items-center justify-between">
      <p class="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        {{ effectiveTitle }}
      </p>
      <Button v-if="helpUrl" as-child variant="link" size="sm" class="h-auto p-0 text-[11px]">
        <a :href="helpUrl" target="_blank" rel="noopener noreferrer">
          {{ t('components.productAssociation.howItWorks') }}
        </a>
      </Button>
    </div>

    <div v-if="$slots.header" class="space-y-2">
      <slot name="header" />
    </div>

    <div v-if="isLoading" class="space-y-3">
      <div class="overflow-hidden rounded-[14px] border border-border">
        <div class="h-9 animate-pulse bg-stone-200" />
        <div
          v-for="i in 3"
          :key="`product-skeleton-${i}`"
          class="flex items-center gap-3 border-t border-border px-4 py-3"
        >
          <div class="h-7 w-7 shrink-0 animate-pulse rounded-[10px] bg-stone-200" />
          <div class="flex-1 space-y-1.5">
            <div class="h-3.5 w-32 animate-pulse rounded bg-stone-200" />
            <div class="h-3 w-20 animate-pulse rounded bg-stone-200" />
          </div>
        </div>
      </div>
      <div
        v-for="i in 2"
        :key="`partner-skeleton-${i}`"
        class="flex items-center justify-between rounded-xl border border-border px-3.5 py-2.5"
      >
        <div class="flex items-center gap-5">
          <div class="h-9 w-9 animate-pulse rounded-lg bg-stone-200" />
          <div class="h-4 w-24 animate-pulse rounded bg-stone-200" />
        </div>
        <div class="h-8 w-24 animate-pulse rounded-md bg-stone-200" />
      </div>
    </div>

    <template v-else>
      <div v-if="hasProducts" class="overflow-hidden rounded-[14px] border border-border">
        <div class="grid items-center gap-4 border-b border-border bg-muted px-4 py-2.5" :class="tableGridClass">
          <p class="text-xs font-semibold uppercase tracking-[0.6px] text-muted-foreground">
            {{ t('components.productAssociation.product') }}
          </p>
          <p
            v-if="showRequiredQuantity"
            class="text-center text-xs font-semibold uppercase tracking-[0.6px] text-muted-foreground"
          >
            {{ t('components.productAssociation.requiredQuantity') }}
          </p>
        </div>

        <ProductRow
          v-for="sku in manualSkus"
          :key="`manual-${sku}`"
          :quantity="getManualSkuQuantity(sku)"
          :table-grid-class="tableGridClass"
          :show-required-quantity="showRequiredQuantity"
          :readonly="readonly"
          @change-quantity="changeQuantity(getManualSkuKey(sku), $event)"
          @remove="handleRemoveSku(sku)"
        >
          <template #info>
            <div class="flex min-w-0 items-center gap-3">
              <div class="flex h-7 w-7 shrink-0 items-center justify-center rounded-[10px] bg-primary/5">
                <BaseIcon name="HeroCubeIconSolid" class="h-3.5 w-3.5 text-primary" />
              </div>
              <div class="min-w-0 flex-1">
                <p class="truncate text-sm font-medium leading-5 text-foreground">{{ sku }}</p>
                <BaseChip :text="t('components.productAssociation.manualProduct')" />
              </div>
            </div>
          </template>
        </ProductRow>

        <ProductRow
          v-for="group in groupedAssociatedProducts"
          :key="group.key"
          :quantity="getGroupQuantity(group)"
          :table-grid-class="tableGridClass"
          :show-required-quantity="showRequiredQuantity"
          :readonly="readonly"
          @change-quantity="changeGroupQuantity(group, $event)"
          @remove="handleRemoveGroup(group)"
        >
          <template #info>
            <div class="flex min-w-0 items-center gap-3">
              <div class="flex h-7 w-7 shrink-0 items-center justify-center rounded-[10px] bg-primary/5">
                <img
                  v-if="group.image && !failedImages.has(group.image)"
                  :src="group.image"
                  :alt="group.name"
                  class="h-full w-full rounded-[10px] object-cover"
                  @error="failedImages.add(group.image!)"
                />
                <BaseIcon v-else name="HeroCubeIconSolid" class="h-3.5 w-3.5 text-primary" />
              </div>
              <div class="min-w-0 flex-1">
                <p class="truncate text-sm font-medium leading-5 text-foreground">
                  {{ group.name }}
                </p>
                <div class="flex items-center gap-1.5 pb-0.5">
                  <span
                    v-if="group.externalIds.length === 1"
                    class="text-xs font-normal leading-5 uppercase text-muted-foreground"
                  >
                    {{ group.externalIds[0] }}
                  </span>
                  <Badge v-else>
                    {{
                      t(
                        'components.productAssociation.storeCount',
                        { count: group.externalIds.length },
                        group.externalIds.length
                      )
                    }}
                  </Badge>
                  <Badge v-if="group.partner" variant="outline">{{ group.partner.name }}</Badge>
                </div>
              </div>
            </div>
          </template>
        </ProductRow>
      </div>

      <div v-if="partners.length > 0" class="space-y-2">
        <p class="text-sm font-medium leading-6 text-foreground">
          {{ effectiveAutomaticInputTitle }}
        </p>
        <div class="divide-y divide-border overflow-hidden rounded-xl border border-border">
          <div
            v-for="partner in partners"
            :key="partner.id"
            class="flex items-center justify-between bg-white px-3.5 py-3.5"
          >
            <div class="flex items-center gap-5">
              <img :src="partner.logo" :alt="`Logo ${partner.name}`" class="h-9 w-9 rounded-lg bg-muted object-cover" />
              <div>
                <p class="text-sm font-medium leading-6 text-foreground">
                  {{ partner.name }}
                </p>
              </div>
            </div>
            <Button v-if="!readonly" variant="outline" data-test="browse-partner" @click="handleOpenModal(partner)">
              {{ t('components.productAssociation.browse') }}
              <Search />
            </Button>
          </div>
        </div>
      </div>

      <div v-else>
        <BaseTip severity="info">
          <template #text>
            <div class="flex items-center">
              <span class="mr-5 flex-1">
                {{ effectiveNoPartnerText }}
              </span>
              <Button variant="link" class="text-indigo-600 hover:text-indigo-700" @click="emit('configure')">
                {{ t('global.words.configure') }}
                <BaseIcon name="HeroArrowRightIconSolid" class="ml-1 h-5 w-5 text-blue-800" />
              </Button>
            </div>
          </template>
        </BaseTip>
      </div>

      <ExternalSkuInput
        :model-value="productSkusCsv"
        :title="effectiveSkuInputTitle"
        :placeholder="effectiveSkuInputPlaceholder"
        :help-text="effectiveSkuInputHelpText"
        :disabled="readonly"
        :show-list="false"
        @update:model-value="productSkusCsv = $event || null"
      />

      <div v-if="$slots.footer" class="space-y-2">
        <slot name="footer" />
      </div>
    </template>

    <p v-if="errorMessage" class="flex items-center gap-1 text-xs font-medium text-red-500">
      <BaseIcon name="HeroExclamationTriangleIconOutline" class="h-4 w-4" />
      {{ errorMessage }}
    </p>
  </div>
</template>
